import React, { useContext, useState } from 'react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
// import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './quill-custom.css';
import TipTapEditor from '~/componentes/TipTapEditor';
import Modal from 'react-modal';

import api from '~/services/api';
// import FormSectionCard from '~/componentes/FormSectionCard';
import InputText from '~/componentes/Inputs/InputText';
// import InputTextarea from '~/componentes/Inputs/InputTextarea';
import InputSelect from '~/componentes/Inputs/InputSelect';
import { SLForm } from '~/pages/Sistema/componentes/SistemaList';
import sistemaListStore, {
  sistemaListShowModal,
} from '~/pages/Sistema/componentes/SistemaList/ThisMainComponent/thisStore';
import ShowConfirmacao from '~/utils/ShowConfirmacao';
import IconLixeira from '~/assets/icones/IconLixeira.svg';
import {
  FileDropZone,
  FileDropZoneContent,
  FilePreview,
  FilePreviewIcon,
  FilePreviewInfo,
  FilePreviewName,
  FilePreviewSize,
  RemoveFileButton,
  AddButton,
  DeleteButton,
  HeaderWrapper,
  TitleWrapper,
  CardTitle,
  CardSubTitle,
  LeiturasContainer,
  // LeiturasFilterContainer,
  LeiturasTable,
  LeiturasTableHeader,
  LeiturasTableBody,
  LeiturasTableEmpty,
  PreviewButton,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  ModalButton,
} from './styles';
import { Colors } from '~/config/Colors';

const schema = z.object({
  titulo: z.string().min(1, 'Título é obrigatório'),
  conteudo: z.string().min(1, 'Conteúdo é obrigatório'),
  ativo: z.boolean(),
  destaque: z.boolean(),
  idTipoContrato: z.string().optional(),
  tipoPerfil: z.string().min(1, 'Tipo de perfil é obrigatório'),
  links: z
    .array(
      z.object({
        url: z
          .string()
          .optional()
          .refine(val => !val || val === '' || z.string().url().safeParse(val).success, {
            message: 'URL deve ser válida',
          }),
      }),
    )
    .optional(),
});

function CreateEditContent({ mountedData, setLoadedData }) {
  const { dispatch } = useContext(sistemaListStore);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [leituras, setLeituras] = useState([]);
  const [isLoadingLeituras, setIsLoadingLeituras] = useState(false);
  const [filtroNome, setFiltroNome] = useState('');
  const [tiposContrato, setTiposContrato] = useState([]);
  const [removedExistingAttachment, setRemovedExistingAttachment] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [uploadedImageFileNames, setUploadedImageFileNames] = useState([]);

  const leiturasFiltradas = React.useMemo(() => {
    if (!filtroNome.trim()) return leituras;
    return leituras.filter(l => {
      const nome = l?.usuario?.nome?.toLowerCase() || '';
      const email = l?.usuario?.email?.toLowerCase() || '';
      const termo = filtroNome.toLowerCase();
      return nome.includes(termo) || email.includes(termo);
    });
  }, [leituras, filtroNome]);

  // Parse links se vier como string JSON
  const parsedLinks = React.useMemo(() => {
    if (!mountedData?.links) return [{ url: '' }];

    let linksArray = mountedData.links;

    // Se for string, fazer parse
    if (typeof linksArray === 'string') {
      try {
        linksArray = JSON.parse(linksArray);
      } catch (e) {
        console.error('Erro ao fazer parse dos links:', e);
        return [{ url: '' }];
      }
    }

    // Se for array e tiver links válidos, retornar
    if (Array.isArray(linksArray) && linksArray.length > 0) {
      const validLinks = linksArray.filter(link => link.url && link.url.trim() !== '');
      return validLinks.length > 0 ? validLinks : [{ url: '' }];
    }

    return [{ url: '' }];
  }, [mountedData]);

  const {
    control,
    formState: { errors },
    handleSubmit: handleFormSubmit,
    register,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: mountedData
      ? {
          titulo: mountedData.titulo || '',
          conteudo: mountedData.conteudo || '',
          ativo: mountedData.ativo ?? true,
          destaque: mountedData.destaque ?? false,
          idTipoContrato: mountedData.idTipoContrato ? String(mountedData.idTipoContrato) : '',
          tipoPerfil: mountedData.tipoPerfil || 'todos',
          links: parsedLinks,
        }
      : {
          titulo: '',
          conteudo: '',
          ativo: true,
          destaque: false,
          idTipoContrato: '',
          tipoPerfil: 'todos',
          links: [{ url: '' }],
        },
  });

  const {
    fields: fieldsLinks,
    append: appendLink,
    remove: removeLink,
  } = useFieldArray({
    control,
    name: 'links',
  });

  // Carregar tipos de contrato
  React.useEffect(() => {
    async function loadTiposContrato() {
      try {
        const response = await api.get('/signed/empresa/administracao/gerenciamento/tiposcontrato');
        setTiposContrato(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Erro ao carregar tipos de contrato:', error);
        setTiposContrato([]);
      }
    }
    loadTiposContrato();
  }, []);

  React.useEffect(() => {
    async function loadLeituras() {
      if (!mountedData?.id) {
        setLeituras([]);
        return;
      }

      setIsLoadingLeituras(true);
      try {
        const response = await api.get(
          `/signed/empresa/gestorprojeto/comunicado/${mountedData.id}/leituras`,
        );
        setLeituras(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Erro ao carregar leituras do comunicado:', error);
        setLeituras([]);
      } finally {
        setIsLoadingLeituras(false);
      }
    }

    loadLeituras();
  }, [mountedData?.id]);

  const hasExistingAttachment =
    !removedExistingAttachment &&
    mountedData?.anexos &&
    Array.isArray(mountedData.anexos) &&
    mountedData.anexos.length > 0;

  const handleFileChange = e => {
    const file = e.target.files[0];
    if (file) {
      const maxSize = 10 * 1024 * 1024;

      if (file.size > maxSize) {
        alert('Arquivo muito grande. O tamanho máximo permitido é 10MB.');
        e.target.value = '';
        return;
      }

      setSelectedFile(file);
      if (hasExistingAttachment) {
        setRemovedExistingAttachment(true);
      }
    }
  };

  const handleDragEnter = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = e => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const file = files[0];
      const maxSize = 10 * 1024 * 1024; // 10MB

      if (file.size > maxSize) {
        alert('Arquivo muito grande. O tamanho máximo permitido é 10MB.');
        return;
      }

      setSelectedFile(file);
      // Se estava mostrando anexo existente, marcar como removido ao selecionar novo
      if (hasExistingAttachment) {
        setRemovedExistingAttachment(true);
      }
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
  };

  const handleRemoveExistingAttachment = () => {
    setRemovedExistingAttachment(true);
  };

  async function handleSubmit(data) {
    return ShowConfirmacao(
      async () => {
        if (uploadedImageFileNames.length > 0) {
          const currentContent = data.conteudo || '';

          const unusedImages = uploadedImageFileNames.filter(fileName => {
            const isInContent = currentContent.includes(fileName);
            console.log(`  - ${fileName}: ${isInContent ? '✓ em uso' : '✗ órfã'}`);
            return !isInContent;
          });

          if (unusedImages.length > 0) {
            await Promise.allSettled(
              unusedImages.map(fileName =>
                api
                  .post('/signed/empresa/gestorprojeto/comunicado/delete-temp-image', { fileName })
                  .then(res => {
                    console.log(`✓ Imagem órfã deletada: ${fileName}`);
                    return res;
                  })
                  .catch(err => {
                    console.error(
                      `✗ Erro ao deletar ${fileName}:`,
                      err?.response?.data || err.message,
                    );
                    throw err;
                  }),
              ),
            );
          } else {
            console.log('✅ Nenhuma imagem órfã detectada');
          }
        }

        const linksValidos = data.links
          ? data.links.filter(link => link.url && link.url.trim() !== '')
          : [];

        const idTipoContrato = data.idTipoContrato ? parseInt(data.idTipoContrato) : null;
        const tipoPerfil = data.tipoPerfil === 'todos' ? null : data.tipoPerfil;

        let thisData = {
          titulo: data.titulo,
          conteudo: data.conteudo,
          ativo: data.ativo,
          destaque: data.destaque,
          idTipoContrato,
          tipoPerfil,
          links: linksValidos,
          removeAttachment: removedExistingAttachment && !selectedFile,
        };

        let response;

        if (selectedFile) {
          const formData = new FormData();
          formData.append('file', selectedFile);
          formData.append('jsonData', JSON.stringify(thisData));

          if (mountedData?.id) {
            response = await api.put(
              `/signed/empresa/gestorprojeto/comunicado?id=${mountedData.id}`,
              formData,
              {
                headers: { 'Content-Type': 'multipart/form-data' },
              },
            );
          } else {
            response = await api.post('/signed/empresa/gestorprojeto/comunicado', formData, {
              headers: { 'Content-Type': 'multipart/form-data' },
            });
          }
        } else {
          // Enviar como JSON quando não houver arquivo
          if (mountedData?.id) {
            response = await api.put(
              `/signed/empresa/gestorprojeto/comunicado?id=${mountedData.id}`,
              thisData,
            );
          } else {
            response = await api.post('/signed/empresa/gestorprojeto/comunicado', thisData);
          }
        }

        if (response?.status === 200) {
          dispatch(sistemaListShowModal(false));
          // Forçar refetch da lista após criar/editar
          if (setLoadedData) {
            setLoadedData(false);
          }
        }
        return response;
      },
      {
        errorModal: {
          defaultMessage: `Ocorreu um erro ao ${
            mountedData?.id ? 'atualizar' : 'cadastrar'
          } esse Comunicado, por favor, tente novamente mais tarde.`,
          customMessage: error => {
            if (error?.response?.data?.error) {
              return error.response.data.error;
            }
            return null;
          },
        },
        message: `Tem certeza que deseja ${
          mountedData?.id ? 'atualizar' : 'cadastrar'
        } esse Comunicado?`,
        successModal: `Comunicado ${mountedData?.id ? 'atualizado' : 'cadastrado'} com sucesso!`,
      },
    );
  }

  return (
    <SLForm onSubmit={handleFormSubmit(handleSubmit)}>
      <div style={{ maxHeight: '70vh', overflowY: 'auto', padding: '0.5rem' }}>
        <div
          style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1rem', padding: '1.4rem' }}
        >
          {/* <FormSectionCard title="Informações do Comunicado">
          </FormSectionCard> */}
          <div style={{ gridColumn: '1 / -1' }}>
            <Controller
              name="titulo"
              control={control}
              render={({ field }) => (
                <InputText
                  label="Título *"
                  placeholder="Digite o título do comunicado"
                  error={errors.titulo?.message}
                  {...field}
                />
              )}
            />
          </div>

          <div style={{ gridColumn: '1 / -1' }}>
            <Controller
              name="conteudo"
              control={control}
              render={({ field }) => (
                <div>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      marginBottom: '0.5rem',
                    }}
                  >
                    <label
                      style={{
                        fontWeight: 500,
                        fontSize: '1.1rem',
                        color: Colors.text_Color_1,
                      }}
                    >
                      Conteúdo *
                    </label>
                    <PreviewButton
                      type="button"
                      onClick={() => setShowPreview(true)}
                      disabled={!field.value || field.value.trim() === ''}
                    >
                      <i className="material-icons" style={{ fontSize: '18px' }}>
                        visibility
                      </i>
                      Visualizar Preview
                    </PreviewButton>
                  </div>

                  <TipTapEditor
                    value={field.value}
                    onChange={field.onChange}
                    onImageUploaded={fileName => {
                      setUploadedImageFileNames(prev => {
                        const updated = [...prev, fileName];
                        return updated;
                      });
                    }}
                    // placeholder="Digite o conteúdo do comunicado. Use o botão 📷 para adicionar imagens!"
                  />

                  {/* EDITOR ANTIGO REACTQUILL - MANTIDO COMENTADO PARA COMPARAÇÃO
                  <div
                    className="quill-no-border"
                    style={{
                      background: Colors.Input_Color_1,
                      borderRadius: '8px',
                    }}
                  >
                    <ReactQuill
                      theme="snow"
                      value={field.value}
                      onChange={field.onChange}
                      placeholder="Digite o conteúdo do comunicado"
                      modules={{
                        toolbar: [
                          [{ header: [1, 2, 3, false] }],
                          ['bold', 'italic', 'underline', 'strike'],
                          [{ list: 'ordered' }, { list: 'bullet' }],
                          [{ color: [] }, { background: [] }],
                          ['link'],
                          ['clean'],
                        ],
                      }}
                    />
                  </div>
                  */}

                  {errors.conteudo?.message && (
                    <span
                      style={{
                        color: '#e74c3c',
                        fontSize: '0.85rem',
                        marginTop: '0.25rem',
                        display: 'block',
                      }}
                    >
                      {errors.conteudo.message}
                    </span>
                  )}
                </div>
              )}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '1rem' }}>
            <Controller
              name="ativo"
              control={control}
              render={({ field }) => (
                <InputSelect
                  label="Ativo *"
                  options={[
                    { value: 'true', label: 'Sim' },
                    { value: 'false', label: 'Não' },
                  ]}
                  error={errors.ativo?.message}
                  {...field}
                  value={String(field.value)}
                  onChange={e => field.onChange(e.target.value === 'true')}
                />
              )}
            />

            <Controller
              name="destaque"
              control={control}
              render={({ field }) => (
                <InputSelect
                  label="Destaque *"
                  options={[
                    { value: 'true', label: 'Sim' },
                    { value: 'false', label: 'Não' },
                  ]}
                  error={errors.destaque?.message}
                  {...field}
                  value={String(field.value)}
                  onChange={e => field.onChange(e.target.value === 'true')}
                />
              )}
            />

            <Controller
              name="idTipoContrato"
              control={control}
              render={({ field }) => (
                <InputSelect
                  label="Tipo de Contrato"
                  options={[
                    { value: '', label: 'Todos' },
                    ...tiposContrato.map(tc => ({ value: String(tc.id), label: tc.sigla })),
                  ]}
                  error={errors.idTipoContrato?.message}
                  {...field}
                />
              )}
            />

            <Controller
              name="tipoPerfil"
              control={control}
              render={({ field }) => (
                <InputSelect
                  label="Tipo de Perfil *"
                  options={[
                    { value: 'todos', label: 'Todos' },
                    { value: 'ADM', label: 'Administrador' },
                    { value: 'GER', label: 'Gerente' },
                    { value: 'COR', label: 'Coordenador' },
                    { value: 'ANA', label: 'Analista' },
                  ]}
                  error={errors.tipoPerfil?.message}
                  {...field}
                />
              )}
            />
          </div>

          {/* Links úteis e Anexo de Arquivo lado a lado */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '1.4rem',
              gridColumn: '1 / -1',
              marginTop: '1rem',
            }}
          >
            {/* Campo de anexo */}
            <div>
              <HeaderWrapper style={{ marginBottom: '0.75rem' }}>
                <TitleWrapper>
                  <CardTitle>Anexar Arquivo</CardTitle>
                  <CardSubTitle>Adicione um arquivo relevante para o comunicado</CardSubTitle>
                </TitleWrapper>
              </HeaderWrapper>

              <FileDropZone
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                isDragging={isDragging}
              >
                <input
                  type="file"
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  id="file-input-create-edit"
                  accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.webp"
                />

                {selectedFile ? (
                  <FilePreview>
                    <FilePreviewIcon>
                      {selectedFile.type?.startsWith('image/') ? '🖼️' : '📄'}
                    </FilePreviewIcon>
                    <FilePreviewInfo>
                      <FilePreviewName>{selectedFile.name}</FilePreviewName>
                      <FilePreviewSize>{(selectedFile.size / 1024).toFixed(2)} KB</FilePreviewSize>
                    </FilePreviewInfo>
                    <RemoveFileButton onClick={handleRemoveFile}>✕</RemoveFileButton>
                  </FilePreview>
                ) : hasExistingAttachment ? (
                  <FilePreview>
                    <FilePreviewIcon>
                      {['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.svg'].includes(
                        (mountedData.anexos[0].arquivo?.ext || '').toLowerCase(),
                      )
                        ? '🖼️'
                        : '📄'}
                    </FilePreviewIcon>
                    <FilePreviewInfo>
                      <FilePreviewName>
                        {mountedData.anexos[0].arquivo?.fileOldName || 'Arquivo anexado'}
                      </FilePreviewName>
                      <FilePreviewSize>
                        {mountedData.anexos[0].arquivo?.tamanho
                          ? `${(mountedData.anexos[0].arquivo.tamanho / 1024).toFixed(2)} KB`
                          : ''}
                      </FilePreviewSize>
                    </FilePreviewInfo>
                    <a
                      href={mountedData.anexos[0].arquivo?.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        color: '#4285f4',
                        textDecoration: 'none',
                        fontSize: '1.2rem',
                        padding: '0 0.5rem',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      onClick={e => e.stopPropagation()}
                      title="Abrir arquivo"
                    >
                      🔗
                    </a>
                    <RemoveFileButton
                      onClick={handleRemoveExistingAttachment}
                      title="Remover anexo"
                    >
                      ✕
                    </RemoveFileButton>
                  </FilePreview>
                ) : (
                  <FileDropZoneContent
                    onClick={() => document.getElementById('file-input-create-edit').click()}
                  >
                    <div style={{ fontSize: '1.6rem', marginBottom: '0.4rem' }}>📎</div>
                    <div style={{ fontSize: '1rem', fontWeight: '500', marginBottom: '0.5rem' }}>
                      {isDragging
                        ? 'Solte o arquivo aqui'
                        : 'Arraste um arquivo ou clique para selecionar'}
                    </div>
                    <div style={{ fontSize: '0.85rem', opacity: 0.7 }}>
                      PDF, DOC, DOCX, XLS, XLSX, PNG, JPG (Máx. 10MB)
                    </div>
                  </FileDropZoneContent>
                )}
              </FileDropZone>
            </div>
            {/* Links úteis */}
            <div>
              <HeaderWrapper style={{ marginBottom: '0.75rem' }}>
                <TitleWrapper>
                  <CardTitle>Links úteis</CardTitle>
                  <CardSubTitle>Adicione links relevantes para o comunicado</CardSubTitle>
                </TitleWrapper>

                <AddButton type="button" onClick={() => appendLink({ url: '' })}>
                  <i
                    className="material-icons"
                    style={{
                      fontSize: '28px',
                      color: localStorage.getItem('tema') == 'E' ? '#ffffff' : '#6796cf',
                    }}
                  >
                    add
                  </i>
                </AddButton>
              </HeaderWrapper>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {fieldsLinks.map((field, index) => (
                  <div key={field.id} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div style={{ position: 'relative', flex: 1 }}>
                      <InputText
                        placeholder="https://..."
                        {...register(`links.${index}.url`)}
                        error={errors.links?.[index]?.url?.message || ''}
                        style={{ paddingRight: '40px' }}
                      />
                    </div>
                    <DeleteButton title="Remover" type="button" onClick={() => removeLink(index)}>
                      <img src={IconLixeira} alt="lixeira" />
                    </DeleteButton>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {mountedData?.id && (
          <LeiturasContainer>
            <CardTitle
              style={{
                fontSize: '1rem',
                margin: '0',
                fontWeight: 600,
              }}
            >
              Usuários que marcaram como lido ({leituras.length})
            </CardTitle>

            {isLoadingLeituras ? (
              <p style={{ fontSize: '0.85rem', opacity: 0.8 }}>Carregando informações...</p>
            ) : leituras.length === 0 ? (
              <p style={{ fontSize: '0.85rem', opacity: 0.8 }}>
                Nenhum usuário marcou este comunicado como lido ainda.
              </p>
            ) : (
              <LeiturasTable>
                <LeiturasTableHeader>
                  <div>Nome</div>
                  <div>E-mail</div>
                  <div>Data de Leitura</div>
                </LeiturasTableHeader>

                {leiturasFiltradas.length === 0 ? (
                  <LeiturasTableEmpty>
                    <i className="material-icons" style={{ fontSize: '32px', opacity: 0.5 }}>
                      search_off
                    </i>
                    <span>Nenhum resultado encontrado para &quot;{filtroNome}&quot;</span>
                  </LeiturasTableEmpty>
                ) : (
                  leiturasFiltradas.map((l, index) => {
                    const nome = l?.usuario?.nome || 'Usuário sem nome';
                    const email = l?.usuario?.email || '-';
                    const data = l?.dataLeitura
                      ? new Date(l.dataLeitura).toLocaleString('pt-BR')
                      : '-';

                    return (
                      <LeiturasTableBody
                        key={`${l.idComunicado}-${l.idUsuario}-${l.dataLeitura || ''}-${index}`}
                      >
                        <div style={{ fontWeight: 500 }}>{nome}</div>
                        <div style={{ opacity: 0.9 }}>{email}</div>
                        <div style={{ opacity: 0.8 }}>{data}</div>
                      </LeiturasTableBody>
                    );
                  })
                )}
              </LeiturasTable>
            )}
          </LeiturasContainer>
        )}
      </div>

      <Modal
        isOpen={showPreview}
        onRequestClose={() => setShowPreview(false)}
        style={{
          overlay: {
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
            zIndex: 99999,
          },
          content: {
            top: '50%',
            left: '50%',
            right: 'auto',
            bottom: 'auto',
            marginRight: '-50%',
            transform: 'translate(-50%, -50%)',
            padding: 0,
            border: 'none',
            borderRadius: '12px',
            maxWidth: '800px',
            width: '90%',
            maxHeight: '90vh',
          },
        }}
      >
        <ModalContent>
          <ModalHeader>
            <ModalTitle>Preview do Comunicado</ModalTitle>
            <ModalCloseButton onClick={() => setShowPreview(false)}>✕</ModalCloseButton>
          </ModalHeader>

          <ModalBody>
            <ModalTitle
              style={{
                color: Colors.Color9,
                marginBottom: '1rem',
              }}
            >
              {control._formValues.titulo || 'Título do comunicado'}
            </ModalTitle>

            <div
              style={{
                lineHeight: '1.6',
              }}
              dangerouslySetInnerHTML={{
                __html: control._formValues.conteudo || '<p>Conteúdo vazio</p>',
              }}
            />

            {control._formValues.links &&
              Array.isArray(control._formValues.links) &&
              control._formValues.links.filter(link => link.url && link.url.trim() !== '').length >
                0 && (
                <div style={{ marginTop: '1.5rem' }}>
                  <strong
                    style={{
                      display: 'block',
                      marginBottom: '0.5rem',
                      color: Colors.text_Color_1,
                    }}
                  >
                    Links úteis
                  </strong>
                  <ul
                    style={{
                      listStyle: 'none',
                      padding: 0,
                      margin: 0,
                      display: 'grid',
                      gap: '0.5rem',
                    }}
                  >
                    {control._formValues.links
                      .filter(link => link.url && link.url.trim() !== '')
                      .map((link, index) => {
                        const linkUrl = link.url;
                        return (
                          <li
                            key={index}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              minWidth: 0,
                            }}
                          >
                            <span
                              style={{
                                fontSize: '0.8rem',
                                flexShrink: 0,
                              }}
                            >
                              🔗
                            </span>
                            <a
                              href={linkUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                fontSize: '1rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                textDecoration: 'underline',
                                color: Colors.Color4,
                              }}
                            >
                              {linkUrl}
                            </a>
                          </li>
                        );
                      })}
                  </ul>
                </div>
              )}

            {(selectedFile || hasExistingAttachment) && (
              <div style={{ marginTop: '1.5rem' }}>
                <strong
                  style={{
                    display: 'block',
                    marginBottom: '0.5rem',
                    color: Colors.text_Color_1,
                  }}
                >
                  Anexo
                </strong>
                <FilePreview>
                  <FilePreviewIcon>
                    {selectedFile
                      ? selectedFile.type?.startsWith('image/')
                        ? '🖼️'
                        : '📄'
                      : hasExistingAttachment &&
                        ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.svg'].includes(
                          (mountedData.anexos[0].arquivo?.ext || '').toLowerCase(),
                        )
                      ? '🖼️'
                      : '📄'}
                  </FilePreviewIcon>
                  <FilePreviewInfo>
                    <FilePreviewName>
                      {selectedFile
                        ? selectedFile.name
                        : mountedData?.anexos?.[0]?.arquivo?.fileOldName || 'Arquivo anexado'}
                    </FilePreviewName>
                    <FilePreviewSize>
                      {selectedFile
                        ? `${(selectedFile.size / 1024).toFixed(2)} KB`
                        : mountedData?.anexos?.[0]?.arquivo?.tamanho
                        ? `${(mountedData.anexos[0].arquivo.tamanho / 1024).toFixed(2)} KB`
                        : ''}
                    </FilePreviewSize>
                  </FilePreviewInfo>
                </FilePreview>
              </div>
            )}
          </ModalBody>

          <ModalFooter>
            <ModalButton secondary type="button" onClick={() => setShowPreview(false)}>
              Fechar
            </ModalButton>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </SLForm>
  );
}

export default CreateEditContent;
